// native
import { BehaviorSubject, Subject } from 'rxjs';
import { Injectable, isDevMode } from '@angular/core';

// constants
import { WEBSOCKET_WEBRTC_STREAM_DEVICE_PATH } from '../../constants';

const STREAM_WAITING_TIMEOUT = 30 * 1000;

@Injectable({
  providedIn: 'root'
})
export class StreamingWebRTCService {

  private peerConnection!: RTCPeerConnection;
  private socket!: WebSocket;
  private isListeningForDevice: boolean = false;
  private rtcConfig = {};

  v2StreamStarted$: Subject<void> = new Subject<void>();
  v2StreamStopped$: Subject<void> = new Subject<void>();
  deviceConnectionFailed$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  statsInterval: any;

  async streamDeviceScreen(guid: string) {
    this.closeWebSocket();
    this.setupWebSocket(guid);
    this.createDeviceReadyTimeout();
  }

  setupWebSocket(guid: string) {
    const protocol = isDevMode() ? 'ws' : 'wss';
    this.socket = new WebSocket(`${protocol}://${window.location.host}/${WEBSOCKET_WEBRTC_STREAM_DEVICE_PATH}${guid}`);

    this.socket.onmessage = async (message) => {
      const data = JSON.parse(message.data);
      if (data.type === 'deviceReady' && this.isListeningForDevice) {
        this.isListeningForDevice = false;
        this.deviceConnectionFailed$.next(false);
        this.v2StreamStarted$.next();
        await this.startStreaming();
      }
      else if (data.type === 'rtcConfig') {
        this.rtcConfig = data.config;
      }
      else if (data.type === 'answer') {
        await this.peerConnection.setRemoteDescription(new RTCSessionDescription(data.answer));
      }
      else if (data.type === 'candidate') {
        await this.peerConnection.addIceCandidate(new RTCIceCandidate(data.candidate));
      }
    };
  }

  async createNewWebRTCConnection() {
    this.peerConnection = new RTCPeerConnection(this.rtcConfig);

    this.peerConnection.onicecandidate = (event) => {
      if (event.candidate) {
        this.socket.send(JSON.stringify({ type: 'candidate', candidate: event.candidate }));
      }
    };

    this.peerConnection.ontrack = (event) => {
      const remoteContainer = document.getElementById('stream_v2_content');

      if (event.streams.length === 0) {
        if (!remoteContainer) return;
        let newStream = new MediaStream();
        newStream.addTrack(event.track);
        this.renderStreamInDiv(remoteContainer, newStream);
      } else {
        this.renderStreamInDiv(remoteContainer, event.streams[0]);
      }
    };

    this.peerConnection.onconnectionstatechange = () => {
      switch (this.peerConnection.connectionState) {
        case 'disconnected':
        case 'failed':
        case 'closed':
          this.isListeningForDevice = true;
          this.deviceConnectionFailed$.next(true);
          setTimeout(() => {
            if (this.isListeningForDevice) {
              this.v2StreamStopped$.next();
              this.clearExistingWebRTCConnection();
            }

          }, STREAM_WAITING_TIMEOUT);
          break;
      }
    };

    // trasceiver needs to be added when there are no tracks to send
    // to be able to send candidates
    this.peerConnection.addTransceiver('video', { direction: 'recvonly' });
    this.peerConnection.addTransceiver('audio', { direction: 'recvonly' });

    // log stats for debug purpose
    this.statsInterval = setInterval(async () => {
      const stats = await this.peerConnection.getStats();
      let bytesSent = 0;
      let bytesReceived = 0;

      stats.forEach(report => {
        if (report.type === "outbound-rtp" && report.kind === "video") {
          bytesSent += report.bytesSent;
        }
        if (report.type === "inbound-rtp" && report.kind === "video") {
          bytesReceived += report.bytesReceived;
        }
      });

      console.log(`Video Sent: ${bytesSent} bytes`);
      console.log(`Video Received: ${bytesReceived} bytes`);
    }, 1000);
  }

  private async startStreaming() {
    this.clearExistingWebRTCConnection();
    await this.createNewWebRTCConnection();

    const offer = await this.peerConnection.createOffer();
    await this.peerConnection.setLocalDescription(offer);
    this.socket.send(JSON.stringify({ type: 'offer', offer }));
  }

  stopStreaming() {
    this.clearExistingWebRTCConnection();
    this.socket?.send(JSON.stringify({ type: 'endStreaming' }));
  }

  clearExistingWebRTCConnection() {
    this.isListeningForDevice = false;
    this.deviceConnectionFailed$.next(false);
    clearInterval(this.statsInterval);
    if (this.peerConnection) {
      this.peerConnection.ontrack = null;
      this.peerConnection.onicecandidate = null;
      this.peerConnection.close();
      this.peerConnection = null;
    }
  }

  closeWebSocket() {
    if (this.socket) {
      this.socket.onmessage = null;
      this.socket.onopen = null;
      this.socket.onclose = null;
      this.socket.onerror = null;

      this.socket.close();
      this.socket = null;
    }
  }

  private renderStreamInDiv(container: HTMLElement | null, stream: MediaStream) {
    if (!container) return;

    const existingVideo = container.querySelector('video');
    if (existingVideo)
      existingVideo.remove();

    const videoElement = document.createElement('video');
    videoElement.srcObject = stream;
    videoElement.autoplay = true;
    videoElement.playsInline = true;
    videoElement.style.width = '100%';
    videoElement.style.height = '100%';
    videoElement.style.objectFit = 'cover';

    container.appendChild(videoElement);
  }

  private createDeviceReadyTimeout() {
    this.isListeningForDevice = true;
    setTimeout(() => {
      if (this.isListeningForDevice) {
        this.isListeningForDevice = false;
        this.v2StreamStopped$.next();
        this.clearExistingWebRTCConnection();
      }
    }, STREAM_WAITING_TIMEOUT);
  }
}
